import CollegeHeroSize from 'assets/images/psp/PSPHero/student-hero.webp';
import FiveIntoFive from 'assets/images/psp/College/5x5.svg';
import FiveIntoTen from 'assets/images/psp/College/5x10.svg';
import TenIntoTen from 'assets/images/psp/College/10x10.svg';
import Location from 'assets/images/psp/College/location.svg';
import Seasonal from 'assets/images/psp/College/seasonal.svg';
import Group from 'assets/images/psp/College/group.svg';
import Planning from 'assets/images/psp/College/planning.svg';
import Roomies from 'assets/images/psp/College/roomies.webp';
import ShutterStock from 'assets/images/psp/College/shutterstock.webp';
import StorageUnitPrice from 'assets/images/psp/College/storage-unit-prices-hero.webp';
import StorageUnitWoman from 'assets/images/psp/College/storage-unit-woman.webp';
import { storageCategories } from 'pages/PSPPage/utils/getPSPLinks';

const reviews = [
	{
		title: 'Very helpful!',
		stars: 5,
		text: 'I spoke with a national rep on the phone who was very helpful. Then shortly after I spoke with the manager from the direct facility and he outlined everything clearly and even put a lock in our unit for us. Very helpful and easy! We were able to do everything online and on the phone since we were getting the unit for our daughter who is in college and we don’t live close by. Thank you!',
		customerName: 'Kimberly DeMaria',
		date: 'May 3, 2025',
		link: 'https://www.trustpilot.com/reviews/68163d3030b211fcba7ea11f',
	},
	{
		title: 'Trip from WA state to get college kid setup was easy.',
		stars: 5,
		text: 'I was here from Washington State trying to get a unit to keep my son’s college stuff safe until Fall. Reached out by phone, a unit was reserved and all information taken care of quickly. The next day we were there when it opened and Sean Bennett was friendly and helped get our unit assigned. One of the last things I need to worry about while on this short trip. Thank you.',
		customerName: 'Dale',
		date: 'Apr 27, 2024',
		link: 'https://www.trustpilot.com/reviews/662bdc70d3e8c7fabc9518e2',
	},
	{
		title: 'Just perfect',
		stars: 5,
		text: 'This was just what I needed. A quick place to store college stuff for a few months. The employee found a place quickly.',
		customerName: 'AL',
		date: 'May 14, 2023',
		link: 'https://www.trustpilot.com/reviews/6460389a746ca7dfea1c9cdb',
	},
	{
		title: 'Great communication',
		stars: 5,
		text: 'So easy to book online. Received a follow up call the next day. Made it easy for my son to store his college stuff.',
		customerName: 'Erika',
		date: 'Jul 21, 2022',
		link: 'https://www.trustpilot.com/reviews/6460389a746ca7dfea1c9cdb',
	},
];

const collegeStudentSelfStorageFAQs = [
	{
		question:
			"What's the cheapest way to store college belongings over summer?",
		answer:
			'The most cost-effective approach depends on your specific situation, but generally, sharing a storage unit with roommates provides the best value.\n\n' +
			'A 10x10 unit split three ways typically costs $25-40 per person monthly, significantly less than maintaining summer housing or multiple moving trips home.\n\n' +
			"Consider your belongings' value when choosing between storage and selling/rebuying items. For basic furniture and appliances, storage often costs less than replacement, especially when you factor in the time and effort required to research and repurchase everything.",
	},
	{
		question:
			'Can I share a storage unit with my friends and/or roommates?',
		answer:
			'Yes, shared storage works well for many college students and provides substantial cost savings. Establish clear agreements upfront about cost splitting, access responsibilities, and belongings organization.\n\n' +
			"Create sections within the unit for each person's belongings and maintain an inventory list to avoid confusion. Discuss access schedules to prevent conflicts, and agree on protocols for adding or removing items.\n\n" +
			'Many students find that shared storage strengthens roommate relationships through cooperative planning and shared responsibility.',
	},
	{
		question: 'How far in advance should I book summer storage?',
		answer:
			"Start researching storage options at least 8-10 weeks before you need them, especially for summer storage when demand peaks. Many facilities offer early booking discounts, and you'll have better selection of unit sizes and locations.\n\n" +
			'Popular campus-area facilities often fill up by March for summer storage needs.\n\n' +
			'If you wait until April or May, you might face limited options and higher prices. Early planning also gives you time to coordinate with roommates, arrange transportation, and take advantage of seasonal promotions.',
	},
	{
		question:
			'What items should I definitely put in climate controlled storage?',
		answer:
			'Electronics, important documents, photographs, and anything sensitive to temperature or humidity changes benefit from climate controlled storage. This includes laptops, gaming systems, musical instruments, artwork, and valuable clothing items.\n\n' +
			'For most college storage needs lasting 3-4 months, standard units work fine for furniture, bedding, and clothing. Climate control becomes more important for longer storage periods or in areas with extreme weather conditions.\n\n' +
			"Consider your belongings' replacement value when deciding whether the extra cost is justified.",
	},
	{
		question: 'How do I organize a storage unit for easy access?',
		answer:
			'Create a pathway down the middle of your unit with frequently needed items toward the front. Use clear bins or detailed labeling so you can identify contents without opening everything. Keep an inventory list on your phone with approximate locations.\n\n' +
			"Store heavy items on the bottom and lighter boxes on top. Leave space around the edges for accessing items along the walls. Consider bringing a flashlight or using your phone's light when visiting your unit, as facility lighting isn't always optimal for detailed searching.",
	},
];

export const CollegeContent = {
	type: 'University',
	reviews: reviews,
	sections: [
		// Hero
		{
			title: 'College Storage Hero',
			background: 'darkBlue',
			modules: [
				{
					type: 'hero',
					props: {
						content: {
							title: 'Find Affordable University Self Storage',
							description:
								'Let’s be real—college life means your living situation changes constantly, but your stuff just keeps piling up.\n\n' +
								"Whether you're heading home for summer break, studying abroad next semester, or finally making the leap from dorm life to your first apartment, you need somewhere to stash your belongings that won't drain your already tight budget.\n\n" +
								'College storage units are the easiest way to handle these transitions without the fuss. **SelfStorage.com** helps you find affordable facilities near campus with flexible rental options that actually work with student schedules and budgets.',
							image: CollegeHeroSize,
							CTAtext: 'Find University Self Storage',
							storageType: 'College',
						},
					},
				},
			],
		},
		{
			title: 'Storage unit size content',
			modules: [
				{
					type: 'cardStrip',
					props: {
						heading: 'Which Storage Unit Size Do You Need?',
						content:
							"**Pro Tip**:\n\n - Measure your largest items (mattress, desk, dresser) before choosing. Most college furniture fits standard dimensions, but that futon or mini-fridge might surprise you.\n\n - Disassembling furniture like bed frames and desks before storage will save you way more space than you'd expect.\n\n - Sharing a bigger unit with a friend will give you the best bang for your buck.",
						cardData: [
							{
								title: '5x5 Storage Unit',
								content:
									'**Perfect for:** Dorm room essentials and seasonal items\n\n' +
									'**Holds:** Bedding, clothes, small appliances, books, desk supplies\n\n' +
									'**Ideal for:** Students living in dorms, minimal possessions, budget-focused\n\n' +
									'**Cost range:** $40-70 per month',
								imageSrc: FiveIntoFive,
								contentAlign: 'text-left',
							},
							{
								title: '5x10 Storage Unit',
								content:
									'**Perfect for:** Apartment furniture plus personal belongings\n\n' +
									'**Holds:** Bedroom furniture, kitchen items, electronics, clothing collection\n\n' +
									'**Ideal for:** Students in off-campus housing, accumulating household items\n\n' +
									'**Cost range:** $60-90 per month',
								imageSrc: FiveIntoTen,
								contentAlign: 'text-left',
							},
							{
								title: '10x10 Storage Unit',
								content:
									'**Perfect for:** Shared apartment contents or multiple students splitting costs\n\n' +
									'**Holds:** Living room furniture, appliances, multiple bedroom sets\n\n' +
									'**Ideal for:** Group storage, senior year accumulation, post-graduation transition\n\n' +
									'**Cost range:** $80-120 per month',
								imageSrc: TenIntoTen,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},

		// SizeGuide
		{
			title: 'Off Campus Storage Guide',
			background: 'gray',
			modules: [
				{
					type: 'cardStrip',
					props: {
						heading: 'Off Campus Storage Guide',
						description:
							"Living off campus creates different storage challenges than dorm life. Your belongings multiply, housing situations change more frequently, and you're responsible for coordinating your own moving logistics.",
						cardData: [
							{
								title: 'Location Considerations',
								content:
									"Choose storage facilities within a reasonable distance of both your current housing and likely future locations. A facility that's convenient to campus serves you better than one that's only close to your current apartment.",
								imageSrc: Location,
								contentAlign: 'text-left',
							},
							{
								title: 'Access Planning',
								content:
									"Consider how often you'll need storage access. Facilities with extended hours or 24/7 access work better for student schedules that don't follow typical business hours.",
								imageSrc: Planning,
								contentAlign: 'text-left',
							},
							{
								title: 'Seasonal Timing',
								content:
									'Book summer storage early, as many facilities offer student discounts but have limited availability during peak moving seasons. Start looking in March for summer storage needs.',
								imageSrc: Seasonal,
								contentAlign: 'text-left',
							},
							{
								title: 'Group Storage Options',
								content:
									'Splitting a larger unit with roommates or friends can provide significant savings. Establish clear agreements about access, costs, and belongings organization before committing.',
								imageSrc: Group,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},

		// SizeGuide
		// {
		// 	title: 'Off Campus Storage Guide',
		// 	noPaddingTop: true,
		// 	modules: [

		// 	],
		// },

		// COntent Section
		{
			title: 'Dorm to Dorm Storage: Mastering the Transition',
			background: 'blueWithMap',
			modules: [
				{
					type: 'content',
					props: {
						title: 'Dorm to Dorm Storage: Mastering the Transition',
						contentType: 'row-type',
						theme: 'dark',
						content: [
							"Moving between dorms presents unique challenges since you're working within institutional timelines and limited transportation options.\n\n" +
								'### Timeline Management\n' +
								'- **8 weeks before:** Research storage options near campus\n' +
								'- **4 weeks before:** Reserve storage unit and arrange transportation\n' +
								'- **2 weeks before:** Start sorting belongings and gathering packing supplies\n' +
								'- **Moving week:** Execute move-out, storage, and move-in phases efficiently\n\n' +
								'**Essential Packing Strategy:** Label boxes clearly with contents and destination (storage vs. new room). Pack a **“first day” box** with immediate necessities that goes directly to your new room, not storage.\n\n' +
								'### Transportation Solutions\n' +
								'- Coordinate with friends who have vehicles\n' +
								'- Rent moving equipment from campus services\n' +
								'- Use ride-sharing services for smaller loads\n' +
								'- Consider professional movers for complex situations\n\n' +
								'### Cost Control Measures\n' +
								'- Share transportation costs with other moving students\n' +
								'- Use free boxes from local stores and campus resources\n' +
								'- Borrow or rent moving equipment rather than buying\n' +
								'- Plan moves during off-peak times when possible',
						],
					},
				},
			],
		},

		//Reviews Section
		{
			title: 'Reviews Section',
			background: 'lighterBlue',
			noPaddingBottom: true,
			modules: [
				{
					type: 'reviews',
					props: {
						reviewsData: {
							slug: 'college-reviews',
							headerTitle: 'College Student Self Storage: ',
							reviews: reviews,
						},
					},
				},
			],
		},

		{
			title: 'College Self Storage FAQs',
			modules: [
				{
					type: 'faqs',
					props: {
						content: {
							header: 'College Self Storage FAQs',
							faqs: collegeStudentSelfStorageFAQs,
						},
					},
				},
			],
		},

		// Related COntent
		{
			title: 'Featured Content',
			background: 'darkBlue',
			modules: [
				{
					type: 'featuredContent',
					props: {
						data: {
							heading: 'Learn All About College Self Storage',
							type: 'unitCards',
							id: 'storage size unit cards',
							data: {
								slug: 'College-featured-content',
								unitCards: [
									{
										title: 'Storage Tips',
										url: 'https://www.selfstorage.com/blog/storage-unit-prices/',
										layout: false,
										plain: true,
										date: 'September 18, 2025',
										image: StorageUnitPrice,
										content:
											'Storage Unit Prices: How Much Is a Storage Unit in 2025?',
									},
									{
										title: 'Moving',
										url: 'https://www.selfstorage.com/blog/the-no-stress-guide-to-moving-in-with-someone-new/',
										layout: false,
										plain: true,
										date: 'September 10, 2025',
										image: Roomies,
										content:
											'A Comprehensive Guide to Moving in with a Roommate',
									},
									{
										title: 'DIY Home & Organization',
										url: 'https://www.selfstorage.com/blog/small-space-living-tips/',
										layout: false,
										plain: true,
										date: 'April 22, 2025',
										image: ShutterStock,
										content:
											'Transformations for Small Space Living: Organizational Ideas for Small Spaces',
									},
									{
										title: 'Storage Tips',
										url: 'https://www.selfstorage.com/blog/cheapest-storage-unit-prices/',
										layout: false,
										plain: true,
										date: 'September 16, 2025',
										image: StorageUnitWoman,
										content:
											'What is the Cheapest Storage Unit You Can Rent?',
									},
								],
							},
						},
					},
				},
			],
		},

		// More Ways to Store
		{
			title: 'Link to other PSPs',
			background: 'lighterBlue',
			modules: [
				{
					type: 'links',
					props: {
						heading: {
							headingName: 'More ways to store',
						},
						linksList: storageCategories.collegeStudent,
					},
				},
			],
		},

		// Near By cities
		{
			title: 'nearby units',
			modules: [
				{
					type: 'storageNearYou',
					props: {
						type: 'University',
					},
				},
			],
		},
	],
};
